import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useClientApi custom hook for client API operations.
 * - Provides CRUD operations for clients using Apollo Client and GraphQL.
 * - Handles cache updates for client queries and mutations.
 * - Exposes helpers for reading/writing/updating clients in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting clients.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const CLIENT_QUERIES = {
  // Query to fetch all clients by status
  GET_CLIENTS: gql`
    query GetClients($status: String!) {
      GetClients(status: $status) {
        client_id
        client_name
        client_code
        client_industry_id
        country_id
        city_id
        city_name
        location
        is_active
        created_on
        updated_on
        client_pocs {
          client_poc_id
          client_id
          poc_designation
          poc_country_id
          poc_name
          poc_email
          poc_mobile
          poc_country_name
          poc_business_unit
          is_active
          created_on
          updated_on
        }
        is_default
      }
    }
  `,

  // Mutation to add a new client
  ADD_CLIENT: gql`
    mutation AddClient(
      $clientName: String!
      $clientCode: String!
      $clientIndustryId: Int!
      $countryId: Int!
      $cityId: Int
      $cityName: String
      $location: String!
      $clientPocs: [ClientPocInput]
    ) {
      AddClient(
        client_name: $clientName
        client_code: $clientCode
        client_industry_id: $clientIndustryId
        country_id: $countryId
        city_id: $cityId
        city_name: $cityName
        location: $location
        client_pocs: $clientPocs
      ) {
        client_id
        client_name
        client_code
        client_industry_id
        country_id
        city_id
        city_name
        location
        is_active
        created_on
        updated_on
        client_pocs {
          client_poc_id
          client_id
          poc_designation
          poc_country_name
          poc_country_id
          poc_business_unit
          poc_name
          poc_email
          poc_mobile
          is_active
          created_on
          updated_on
        }
        is_default
      }
    }
  `,

  // Mutation to update an existing client
  UPDATE_CLIENT: gql`
    mutation UpdateClient(
      $clientId: ID!
      $clientName: String!
      $clientCode: String!
      $clientIndustryId: Int!
      $countryId: Int!
      $cityId: Int
      $cityName: String
      $location: String!
      $isActive: Boolean
      $clientPocs: [ClientPocUpdateInput]
    ) {
      UpdateClient(
        client_id: $clientId
        client_name: $clientName
        client_code: $clientCode
        client_industry_id: $clientIndustryId
        country_id: $countryId
        city_id: $cityId
        city_name: $cityName
        location: $location
        is_active: $isActive
        client_pocs: $clientPocs
      ) {
        client_id
        client_name
        client_code
        client_industry_id
        country_id
        city_id
        city_name
        location
        is_active
        created_on
        updated_on
        client_pocs {
          client_poc_id
          client_id
          poc_designation
          poc_country_id
          poc_country_name
          poc_business_unit
          poc_name
          poc_email
          poc_mobile
          is_active
          created_on
          updated_on
        }
        is_default
      }
    }
  `,

  // Mutation to delete a client
  DELETE_CLIENT: gql`
    mutation DeleteClient($clientId: ID!) {
      DeleteClient(client_id: $clientId) {
        client_id
        client_name
        client_code
        client_industry_id
        country_id
        city_id
        city_name
        location
        is_active
        created_on
        updated_on
        client_pocs {
          client_poc_id
          client_id
          poc_designation
          poc_country_id
          poc_name
          poc_email
          poc_mobile
          poc_country_name
          poc_business_unit
          is_active
          created_on
          updated_on
        }
        is_default
      }
    }
  `,

  // Query to fetch client POCs by client ID
  GET_CLIENT_POC: gql`
    query GetClientPocByClientId($clientId: ID!) {
      GetClientPocByClientId(client_id: $clientId) {
        client_poc_id
        client_id
        poc_designation
        poc_country_id
        poc_name
        poc_email
        poc_mobile
        poc_country_name
        poc_business_unit
        is_active
        created_on
        updated_on
      }
    }
  `,
};

// Cache operations for clients
const cacheOperations = {
  /**
   * Reads the clients array from Apollo Client cache.
   * @returns {Array} Array of client objects or empty array if not found.
   */
  readClients: () => {
    try {
      const { GetClients = [] } =
        client.readQuery({ query: CLIENT_QUERIES.GET_CLIENTS }) || {};
      return GetClients;
    } catch {
      return [];
    }
  },
  /**
   * Writes the provided clients array to Apollo Client cache.
   * @param {Array} clients - Array of client objects.
   */
  writeClients: (clients) => {
    client.writeQuery({
      query: CLIENT_QUERIES.GET_CLIENTS,
      data: { GetClients: clients },
    });
  },
  /**
   * Updates a client in the Apollo Client cache.
   * @param {Object} updatedClient - The updated client object.
   */
  updateClientInCache: (updatedClient) => {
    const existingClients = cacheOperations.readClients();
    const updatedClients = existingClients.map((client) =>
      client.client_id === updatedClient.client_id ? updatedClient : client
    );
    cacheOperations.writeClients(updatedClients);
  },
};

export const useClientApi = () => {
  // Mutation for adding a client
  const [addClientMutation] = useMutation(CLIENT_QUERIES.ADD_CLIENT, {
    update(cache, { data: { AddClient } }) {
      try {
        const existingData = cache.readQuery({
          query: CLIENT_QUERIES.GET_CLIENTS,
        });
        if (existingData) {
          cache.writeQuery({
            query: CLIENT_QUERIES.GET_CLIENTS,
            data: {
              GetClients: [AddClient, ...existingData.GetClients],
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after adding client:", error);
      }
    },
  });

  // Mutation for updating a client
  const [updateClientMutation] = useMutation(CLIENT_QUERIES.UPDATE_CLIENT, {
    update(cache, { data: { UpdateClient } }) {
      try {
        const existingData = cache.readQuery({
          query: CLIENT_QUERIES.GET_CLIENTS,
        });
        if (existingData) {
          const newData = existingData.GetClients.map((client) =>
            client.client_id === UpdateClient.client_id ? UpdateClient : client
          );
          cache.writeQuery({
            query: CLIENT_QUERIES.GET_CLIENTS,
            data: { GetClients: newData },
          });
        }
      } catch (error) {
        console.error("Error updating cache after updating client:", error);
      }
    },
  });

  // Mutation for deleting a client
  const [deleteClientMutation] = useMutation(CLIENT_QUERIES.DELETE_CLIENT, {
    update(cache, { data: { DeleteClient } }) {
      try {
        const existingData = cache.readQuery({
          query: CLIENT_QUERIES.GET_CLIENTS,
        });
        if (existingData) {
          cache.writeQuery({
            query: CLIENT_QUERIES.GET_CLIENTS,
            data: {
              GetClients: existingData.GetClients.filter(
                (client) => client.client_id !== DeleteClient.client_id
              ),
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after deleting client:", error);
      }
    },
  });

  /**
   * Fetches all clients from the backend by status.
   * @param {string} status - Status filter for clients.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getClients = async (status) => {
    try {
      const { data } = await client.query({
        query: CLIENT_QUERIES.GET_CLIENTS,
        fetchPolicy: "network-only",
        variables: { status },
      });

      if (!data || !data.GetClients) {
        throw new Error("No data received from getClients");
      }

      return { success: true, data: data.GetClients };
    } catch (error) {
      console.error("Error in getClients:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a client by its ID.
   * @param {string|number} client_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getClientById = async (client_id) => {
    try {
      const { data } = await client.query({
        query: CLIENT_QUERIES.GET_CLIENT_BY_ID,
        variables: { client_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetClientById };
    } catch (error) {
      console.error("Error fetching client:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches client POCs by client ID.
   * @param {string|number} client_id
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getClientPocByClientId = async (client_id) => {
    try {
      const { data } = await client.query({
        query: CLIENT_QUERIES.GET_CLIENT_POC,
        variables: { clientId: client_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetClientPocByClientId };
    } catch (error) {
      console.error("Error fetching client POCs:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new client.
   * @param {Object} formData - Client form data.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createClient = async (formData) => {
    try {
      const { data } = await addClientMutation({
        variables: {
          clientName: formData.client_name,
          clientCode: formData.client_code,
          clientIndustryId: parseInt(formData.client_industry_id),
          countryId: parseInt(formData.country_id),
          cityName: formData.city_name,
          cityId: parseInt(formData.city_id),
          location: formData.location,
          isActive: formData.is_active,
          clientPocs: formData.client_pocs,
        },
      });

      if (!data?.AddClient) {
        throw new Error("Failed to create client");
      }

      return { success: true, data: data.AddClient };
    } catch (error) {
      // Handle GraphQL errors properly
      if (error.graphQLErrors?.length > 0) {
        return {
          success: false,
          error: error.graphQLErrors[0].message,
        };
      }
      return {
        success: false,
        error: error.message || "An error occurred while creating the client",
      };
    }
  };

  /**
   * Updates an existing client.
   * @param {string|number} clientId
   * @param {Object} formData - Client form data.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateClient = async (clientId, formData) => {
    try {
      const { data } = await updateClientMutation({
        variables: {
          clientId: clientId.toString(),
          clientName: formData.client_name,
          clientCode: formData.client_code,
          clientIndustryId: parseInt(formData.client_industry_id),
          countryId: parseInt(formData.country_id),
          cityName: formData.city_name,
          cityId: parseInt(formData.city_id),
          location: formData.location,
          isActive: formData.is_active,
          clientPocs: formData.client_pocs,
        },
      });

      return { success: true, data: data.UpdateClient };
    } catch (error) {
      console.error("Error updating client:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a client by its ID.
   * @param {string|number} client_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteClient = async (client_id) => {
    try {
      const { data } = await deleteClientMutation({
        variables: { clientId: client_id },
      });

      return { success: true, data: data.DeleteClient };
    } catch (error) {
      console.error("Error deleting client:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getClients,
    getClientById,
    createClient,
    updateClient,
    deleteClient,
    getClientPocByClientId,
    cacheOperations,
  };
};
